import CheckBox from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';
import Close from '@mui/icons-material/Close';
import Autocomplete, { type AutocompleteProps } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import React, { forwardRef } from 'react';

import type { WithRequired } from '../../utils/types';
import { InfoIconButton } from '../InfoIconButton';
import { TextField } from '../TextField/TextField';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

export type MultiAutocompleteProps<T> = WithRequired<
  Omit<AutocompleteProps<T, true, false, false>, 'renderInput'>,
  'isOptionEqualToValue' | 'getOptionLabel'
> & {
  helperText?: string;
  label?: string;
  required?: boolean;
  error?: string;
  name?: string;
  infoTooltip?: string;
  placeholder?: string;
};

const Field = <T,>(
  {
    id,
    label,
    fullWidth = true,
    required,
    error,
    value,
    helperText,
    name,
    placeholder = 'Type to search',
    infoTooltip,
    size,
    ...rest
  }: MultiAutocompleteProps<T>,
  ref: React.ForwardedRef<HTMLInputElement>
) => {
  const hasError = Boolean(error);
  return (
    <FormControl
      required={required}
      error={hasError}
      fullWidth={fullWidth}
      sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}
    >
      <Box
        sx={{
          width: fullWidth ? '100%' : null,
        }}
      >
        <Autocomplete
          id={id}
          disablePortal
          multiple
          fullWidth={fullWidth}
          value={value || undefined}
          disableCloseOnSelect
          clearIcon={<Close data-testid="CloseIcon" />}
          renderOption={(props, option, { selected }) => {
            const { key, onClick, ...optionProps } = props;
            return (
              <li
                {...optionProps}
                key={key}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClick?.(e);
                }}
              >
                <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} sx={{ marginRight: 0.5 }} />
                {rest.getOptionLabel(option)}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus={rest.autoFocus}
              placeholder={placeholder}
              required={required}
              size={size}
              label={label}
              error={hasError}
              name={name}
              loading={rest.loading}
            />
          )}
          {...rest}
          ref={ref}
        />
        {hasError ? <FormHelperText>{error}</FormHelperText> : null}
        {helperText && !hasError ? <FormHelperText>{helperText}</FormHelperText> : null}
      </Box>
      {infoTooltip ? <InfoIconButton tooltip={infoTooltip} sx={{ marginTop: 1 }} /> : null}
    </FormControl>
  );
};

export const MultiAutocomplete = forwardRef(Field) as <T>(props: MultiAutocompleteProps<T>) => React.ReactElement;
